/**
 * useDeviceEvents Hook
 *
 * Custom hook for handling device-related WebSocket events.
 * Manages device state updates, claimed/unclaimed events, and device notifications.
 *
 * Phase 3B: Device Events Extraction
 *
 * Events handled:
 * - deviceStateUpdate: Update device state (lockState, lockdownState, doorPosition, etc.)
 * - deviceClaimed: Handle new device added to installation
 * - deviceUnclaimed: Handle device removed from installation
 * - DEVICE_NOTIFICATION_REPORTED: Process device notifications (lockdown, connectivity, etc.)
 *
 * @module hooks/useDeviceEvents
 */

import { useCallback } from "react";
import { useDevicesStore, useBulkOperationsStore, useUIStore } from "../stores";
import logger from "../utils/logger";

/**
 * Helper function to check if a state value is a valid lock mechanism state
 *
 * Valid lock mechanism states: LOCKED, UNLOCKED, PASSAGE, SECURED, UNKNOWN
 * These should be stored in the lockState field, not lockdownState
 *
 * @param {string} state - The state value to check
 * @returns {boolean} True if the state is a valid lock mechanism state
 */
const isValidLockMechanismState = (state) => {
  const lockMechanismStates = [
    "LOCKED",
    "UNLOCKED",
    "PASSAGE",
    "SECURED",
    "UNKNOWN",
  ];
  return lockMechanismStates.includes(String(state).trim().toUpperCase());
};

/**
 * Helper function to check if a state value is "Event Denied"
 *
 * Backend sometimes incorrectly sends "Event Denied" in state fields
 * instead of using DEVICE_NOTIFICATION_REPORTED event.
 * This should be filtered out from state updates.
 *
 * @param {string} value - The value to check
 * @returns {boolean} True if the value contains "Event Denied"
 */
const isEventDenied = (value) => {
  const upperValue = String(value).trim().toUpperCase();
  return upperValue.includes("EVENT") && upperValue.includes("DENIED");
};

/**
 * Custom hook for handling device-related WebSocket events
 *
 * @param {Object} params - Hook parameters
 * @param {string} params.installationId - Current installation ID
 * @param {string} params.selectedDeviceId - Currently selected device ID
 * @param {Object} params.selectedDeviceIdRef - Ref to selected device ID (for callbacks)
 * @param {Function} params.authenticatedFetch - Authenticated fetch function
 * @param {Function} params.invalidateInstallationSyncCache - Function to invalidate installation cache
 * @param {Function} params.showNotification - Function to show notifications
 * @param {Object} params.config - Configuration object with API endpoints
 *
 * @returns {Object} Object with device event handler functions
 * @returns {Function} handleDeviceStateUpdate - Handler for deviceStateUpdate events
 * @returns {Function} handleDeviceClaimed - Handler for deviceClaimed events
 * @returns {Function} handleDeviceUnclaimed - Handler for deviceUnclaimed events
 * @returns {Function} handleDeviceNotificationReported - Handler for DEVICE_NOTIFICATION_REPORTED events
 */
export const useDeviceEvents = ({
  installationId,
  selectedDeviceId,
  selectedDeviceIdRef,
  authenticatedFetch,
  invalidateInstallationSyncCache,
  showNotification,
  config,
}) => {
  // Access Zustand stores
  const {
    setDevices,
    setDeviceStates,
    updateDeviceState,
    setSelectedDeviceId,
    setConnectivityStatusUpdateTime,
  } = useDevicesStore();

  // Event denied alerts are in UIStore
  const { setEventDeniedAlert, markDeviceUpdateReady } = useUIStore();

  // Bulk operations state (Phase 1 Days 43-45)
  const { setBulkDeviceLockdownStates } = useBulkOperationsStore();

  /**
   * Handler for deviceStateUpdate events
   *
   * Updates device state (lockState, lockdownState, doorPosition, etc.)
   * Includes filtering logic for "Event Denied" and backend bug workarounds
   *
   * Implementation: Phase 3B PR #4
   *
   * @param {Object} message - WebSocket message
   */
  const handleDeviceStateUpdate = useCallback(
    (message) => {
      const { deviceId, ...stateUpdates } = message;

      logger.log(`[deviceStateUpdate] 📡 Received for device ${deviceId}`);
      logger.log(`[deviceStateUpdate] Original stateUpdates:`, stateUpdates);
      logger.log(
        `[deviceStateUpdate] 🕐 Timestamp: ${new Date().toISOString()}`,
      );
      logger.log(
        `[deviceStateUpdate] 🔑 Full message keys:`,
        Object.keys(message),
      );

      // STEP 1: Filter out "Event Denied" from BOTH lockdownState and lockState FIRST
      // Event Denied should never be saved to database - it's a notification event
      if ("lockdownState" in stateUpdates) {
        const lockdownStateValue = String(stateUpdates.lockdownState)
          .trim()
          .toUpperCase();
        logger.log(
          `[deviceStateUpdate] STEP 1a: Checking lockdownState value: "${lockdownStateValue}" (original: "${stateUpdates.lockdownState}")`,
        );
        if (isEventDenied(stateUpdates.lockdownState)) {
          logger.log(
            `[deviceStateUpdate] ❌ STEP 1a: Filtering out Event Denied from lockdownState for device ${deviceId}`,
          );
          logger.log(
            `[deviceStateUpdate] ⚠️ NOTE: Backend should send DEVICE_NOTIFICATION_REPORTED event instead!`,
          );
          delete stateUpdates.lockdownState;
          // Event Denied detected - skip all further processing for this update
          logger.log(
            `[deviceStateUpdate] ⏭️ Breaking out early due to Event Denied detection`,
          );
          return;
        }
      }

      if ("lockState" in stateUpdates) {
        const lockStateValue = String(stateUpdates.lockState)
          .trim()
          .toUpperCase();
        logger.log(
          `[deviceStateUpdate] STEP 1b: Checking lockState value: "${lockStateValue}" (original: "${stateUpdates.lockState}")`,
        );
        if (isEventDenied(stateUpdates.lockState)) {
          logger.log(
            `[deviceStateUpdate] ❌ STEP 1b: Filtering out Event Denied from lockState for device ${deviceId}`,
          );
          delete stateUpdates.lockState;
          // Event Denied detected - skip all further processing for this update
          logger.log(
            `[deviceStateUpdate] ⏭️ Breaking out early due to Event Denied detection`,
          );
          return;
        }
      }

      // STEP 2: TEMPORARY FIX: Backend sometimes sends lock mechanism state in "lockdownState" field
      // Only map to lockState if it's a valid lock mechanism state (not a lockdown state)
      // Valid lockdown states: "Lockdown On", "Lockdown Off"
      // Valid lock mechanism states: "Locked", "Unlocked", "Passage", etc.
      if ("lockdownState" in stateUpdates && !("lockState" in stateUpdates)) {
        const value = stateUpdates.lockdownState;
        logger.log(
          `[deviceStateUpdate] STEP 2: Found lockdownState field with value: "${value}"`,
        );

        const upperValue = String(value).trim().toUpperCase();

        logger.log(
          `[deviceStateUpdate] STEP 2: Comparing "${upperValue}" against valid states:`,
          ["LOCKED", "UNLOCKED", "PASSAGE", "SECURED", "UNKNOWN"],
        );

        if (isValidLockMechanismState(value)) {
          logger.log(
            `[deviceStateUpdate] ✅ STEP 2: Mapping lock mechanism state "${value}" to lockState field`,
          );
          stateUpdates.lockState = value;
          delete stateUpdates.lockdownState;
        } else if (value === "Lockdown On" || value === "Lockdown Off") {
          logger.log(
            `[deviceStateUpdate] ✅ STEP 2: Keeping lockdownState "${value}" as valid lockdown state`,
          );
        } else {
          logger.log(
            `[deviceStateUpdate] ⚠️ STEP 2: Unknown state "${value}" in lockdownState field - keeping as-is`,
          );
        }
      }

      logger.log(
        `[deviceStateUpdate] ✅ Final stateUpdates after all filtering:`,
        stateUpdates,
      );
      logger.log(
        `[deviceStateUpdate] 🏁 Processing complete for device ${deviceId}`,
      );

      // Track bulk action device lockdown state changes
      // Only update if the value is "Lockdown On" or "Lockdown Off"
      // Note: This uses module-level tracking in AppContent.js (not migrated to hook)
      if ("lockdownState" in stateUpdates) {
        const lockdownState = stateUpdates.lockdownState;

        if (
          lockdownState === "Lockdown On" ||
          lockdownState === "Lockdown Off"
        ) {
          logger.log(
            `[deviceStateUpdate] 🔒 Device ${deviceId} lockdownState: ${lockdownState} (bulk tracking handled in AppContent)`,
          );
        }
      }

      // Enhanced logging for lockState updates (especially FROZEN states)
      if ("lockState" in stateUpdates) {
        const lockStateValue = String(stateUpdates.lockState).toUpperCase();
        if (lockStateValue.includes("FROZEN")) {
          logger.log(
            `[deviceStateUpdate] ❄️ FROZEN LOCK STATE - Device: ${deviceId}, lockState: ${stateUpdates.lockState}`,
          );
        }
      }

      // Log door position updates
      if ("doorPosition" in stateUpdates) {
        logger.log(
          `[deviceStateUpdate] 🚪 DOOR POSITION UPDATE - Device: ${deviceId}, doorPosition: ${stateUpdates.doorPosition}`,
        );
      }

      // Log if we have BOTH lockdownState and lockState in same update
      if ("lockdownState" in stateUpdates && "lockState" in stateUpdates) {
        logger.log(
          `[deviceStateUpdate] 🔄 DUAL UPDATE - Device: ${deviceId}, lockdownState: ${stateUpdates.lockdownState}, lockState: ${stateUpdates.lockState}`,
        );
      }

      // Update Zustand store state for bulk lockdown tracking
      if ("lockdownState" in stateUpdates) {
        const lockdownState = stateUpdates.lockdownState;

        if (
          lockdownState === "Lockdown On" ||
          lockdownState === "Lockdown Off"
        ) {
          setBulkDeviceLockdownStates((prevStates) => {
            logger.log(
              `[deviceStateUpdate] 💾 Updating bulk device lockdown state for ${deviceId}: ${lockdownState}`,
            );
            logger.log(`[deviceStateUpdate] Previous states:`, prevStates);
            const newStates = {
              ...prevStates,
              [deviceId]: lockdownState,
            };
            logger.log(`[deviceStateUpdate] New states:`, newStates);
            return newStates;
          });
        }
      }

      // Update device state in Zustand store
      const currentDeviceStates = useDevicesStore.getState().deviceStates;
      const updatedDeviceState = {
        ...currentDeviceStates[deviceId],
        ...stateUpdates,
      };
      logger.log(
        `[deviceStateUpdate] 💾 Updating deviceStates for ${deviceId}:`,
        {
          previousState: currentDeviceStates[deviceId],
          stateUpdates: stateUpdates,
          newState: updatedDeviceState,
          lockStateChanged:
            currentDeviceStates[deviceId]?.lockState !==
            updatedDeviceState.lockState,
          previousLockState: currentDeviceStates[deviceId]?.lockState,
          newLockState: updatedDeviceState.lockState,
        },
      );
      updateDeviceState(deviceId, updatedDeviceState);

      // Signal the DeviceUpdateBanner when device reports ONLINE connectivity.
      // The backend transforms DEVICE_NOTIFICATION_REPORTED into deviceStateUpdate,
      // so ONLINE connectivity arrives here — not in handleDeviceNotificationReported.
      // markDeviceUpdateReady is a no-op if the banner isn't open for this device.
      if (stateUpdates.connectivityStatus === "ONLINE") {
        markDeviceUpdateReady(deviceId);
      }

      // For WiFi network changes (WifiSettingsV1ConnectStandardAccessPoint) the device
      // never goes offline — it simply switches networks. ONLINE connectivity is therefore
      // never re-reported, so we use WiFi state field updates as the signal that the
      // device has successfully reported its new state.
      if (
        stateUpdates.activeSsid !== undefined ||
        stateUpdates.activeSignalStrength !== undefined ||
        stateUpdates.activeAccessPointType !== undefined
      ) {
        markDeviceUpdateReady(deviceId);
      }

      // --- Toast notifications for state transitions (selected device only) ---
      if (deviceId === selectedDeviceIdRef.current) {
        const prevState = currentDeviceStates[deviceId] || {};
        const devices = useDevicesStore.getState().devices;
        const device = devices.find((d) => d.deviceId === deviceId);
        const deviceName = device?.displayName || "Device";

        // Door position: toast on OPEN/SHUT transitions
        if (stateUpdates.doorPosition !== undefined && stateUpdates.doorPosition !== prevState.doorPosition) {
          if (stateUpdates.doorPosition === "OPEN") {
            showNotification({ message: `${deviceName}: Door opened`, type: "info" });
          } else if (stateUpdates.doorPosition === "SHUT") {
            showNotification({ message: `${deviceName}: Door closed`, type: "info" });
          }
        }

        // Connectivity: toast on ONLINE/OFFLINE transitions
        if (stateUpdates.connectivityStatus !== undefined && stateUpdates.connectivityStatus !== prevState.connectivityStatus) {
          if (stateUpdates.connectivityStatus === "ONLINE") {
            showNotification({ message: `${deviceName}: Back online`, type: "success" });
          } else if (stateUpdates.connectivityStatus === "OFFLINE") {
            showNotification({ message: `${deviceName}: Went offline`, type: "warning" });
          }
        }

        // Battery: toast on LOW/CRITICAL transitions
        if (stateUpdates.batteryLevel !== undefined && stateUpdates.batteryLevel !== prevState.batteryLevel) {
          if (stateUpdates.batteryLevel === "LOW") {
            showNotification({ message: `${deviceName}: Battery low`, type: "warning" });
          } else if (stateUpdates.batteryLevel === "CRITICAL") {
            showNotification({ message: `${deviceName}: Battery critical — replace soon`, type: "error" });
          }
        }
      }

      // Update connectivity status timestamp
      if ("connectivityStatus" in stateUpdates) {
        const newTime = new Date().toISOString();
        setConnectivityStatusUpdateTime((prevTimes) => {
          const newTimes = { ...prevTimes, [deviceId]: newTime };
          localStorage.setItem(
            "connectivityStatusUpdateTimes",
            JSON.stringify(newTimes),
          );
          return newTimes;
        });
      }

      // Update device display name if changed
      if ("displayName" in stateUpdates) {
        setDevices((prevDevices) => {
          let needsSort = false;
          const updated = prevDevices.map((dev) => {
            if (
              dev.deviceId === deviceId &&
              dev.displayName !== stateUpdates.displayName
            ) {
              needsSort = true;
              return { ...dev, displayName: stateUpdates.displayName };
            }
            return dev;
          });
          return needsSort
            ? updated.sort((a, b) =>
                (a.displayName || a.serialNumber || a.deviceId).localeCompare(
                  b.displayName || b.serialNumber || b.deviceId,
                ),
              )
            : prevDevices;
        });
      }
    },
    [
      setDevices,
      updateDeviceState,
      setBulkDeviceLockdownStates,
      setConnectivityStatusUpdateTime,
      markDeviceUpdateReady,
    ],
  );

  /**
   * Handler for deviceClaimed events
   *
   * Adds newly claimed device to the installation's device list
   * Initializes device state and triggers data refresh
   *
   * Implementation: Phase 3B PR #2
   *
   * @param {Object} message - WebSocket message
   */
  const handleDeviceClaimed = useCallback(
    (message) => {
      logger.log("[deviceClaimed] Device claimed event received:", message);
      const { deviceId, device } = message;

      if (deviceId && device && device.installationId === installationId) {
        logger.log(
          `[deviceClaimed] Adding device ${deviceId} to installation ${installationId}`,
        );

        setDevices((prevDevices) => {
          // Check if device already exists
          const exists = prevDevices.some((d) => d.deviceId === deviceId);
          if (exists) {
            logger.log(
              `[deviceClaimed] Device ${deviceId} already in list, skipping`,
            );
            return prevDevices;
          }

          const newDevices = [...prevDevices, device];
          // Sort by display name for consistency
          const sorted = newDevices.sort((a, b) =>
            (a.displayName || a.serialNumber || a.deviceId).localeCompare(
              b.displayName || b.serialNumber || b.deviceId,
            ),
          );
          logger.log(
            `[deviceClaimed] Device list updated, total devices: ${sorted.length}`,
          );

          // Auto-select the new device if no device is selected or this is the first device
          if (!selectedDeviceIdRef.current || prevDevices.length === 0) {
            logger.log(
              `[deviceClaimed] Auto-selecting newly claimed device ${deviceId}`,
            );
            setSelectedDeviceId(deviceId);
          }

          return sorted;
        });

        // Initialize device state with defaults
        setDeviceStates((prevStates) => ({
          ...prevStates,
          [deviceId]: {
            deviceId,
            connectivityStatus: device.connectivityStatus || "UNKNOWN",
            lastUpdated: Date.now(),
          },
        }));

        // Trigger a data refresh to get full device state
        logger.log(
          `[deviceClaimed] Triggering refresh to fetch full device state`,
        );

        // Invalidate installation sync cache (device list changed)
        invalidateInstallationSyncCache(installationId);

        authenticatedFetch(`${config.apiEndpoint}/refresh-data`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }).catch((error) => {
          logger.warn(`[deviceClaimed] Auto-refresh failed:`, error);
        });

        showNotification({
          message: `Device ${
            device.displayName || device.serialNumber || deviceId
          } added successfully!`,
          type: "success",
        });
      }
    },
    [
      installationId,
      selectedDeviceIdRef,
      setDevices,
      setDeviceStates,
      setSelectedDeviceId,
      invalidateInstallationSyncCache,
      authenticatedFetch,
      showNotification,
      config,
    ],
  );

  /**
   * Handler for deviceUnclaimed events
   *
   * Removes unclaimed device from the installation's device list
   * Cleans up device state and handles device selection
   *
   * Implementation: Phase 3B PR #2
   *
   * @param {Object} message - WebSocket message
   */
  const handleDeviceUnclaimed = useCallback(
    (message) => {
      logger.log("[deviceUnclaimed] Device unclaimed event received:", message);
      const { deviceId } = message;

      if (deviceId) {
        logger.log(
          `[deviceUnclaimed] Removing device ${deviceId} from installation`,
        );

        const wasSelected = deviceId === selectedDeviceIdRef.current;

        setDevices((prevDevices) => {
          const filtered = prevDevices.filter((d) => d.deviceId !== deviceId);
          logger.log(
            `[deviceUnclaimed] Device list updated, remaining devices: ${filtered.length}`,
          );

          // Auto-select first device if the removed device was selected
          if (wasSelected && filtered.length > 0) {
            logger.log(
              `[deviceUnclaimed] Auto-selecting first remaining device`,
            );
            setSelectedDeviceId(filtered[0].deviceId);
          } else if (wasSelected && filtered.length === 0) {
            logger.log(`[deviceUnclaimed] No devices left, clearing selection`);
            setSelectedDeviceId("");
          }

          return filtered;
        });

        // Clean up device state
        setDeviceStates((prevStates) => {
          const { [deviceId]: removed, ...remaining } = prevStates;
          logger.log(`[deviceUnclaimed] Removed device state for ${deviceId}`);
          return remaining;
        });

        // Clean up bulk lockdown state
        setBulkDeviceLockdownStates((prevStates) => {
          const { [deviceId]: removed, ...remaining } = prevStates;
          logger.log(
            `[deviceUnclaimed] Removed bulk lockdown state for ${deviceId}`,
          );
          return remaining;
        });

        // Invalidate installation sync cache (device list changed)
        invalidateInstallationSyncCache(installationId);

        showNotification({
          message: `Device removed successfully!`,
          type: "success",
        });
      }
    },
    [
      selectedDeviceIdRef,
      setDevices,
      setDeviceStates,
      setBulkDeviceLockdownStates,
      setSelectedDeviceId,
      invalidateInstallationSyncCache,
      installationId,
      showNotification,
    ],
  );

  /**
   * Handler for DEVICE_NOTIFICATION_REPORTED events
   *
   * Processes device notifications including:
   * - EVENT_DENIED notifications (with lockdown checks)
   * - Lockdown state changes (bulk action tracking)
   * - Lock state changes (deadbolt/passage mode)
   * - Connectivity status updates
   *
   * Implementation: Phase 3B PR #3
   *
   * @param {Object} message - WebSocket message
   */
  const handleDeviceNotificationReported = useCallback(
    (message) => {
      logger.log("[DEVICE_NOTIFICATION_REPORTED] 🔔 Received event");
      logger.log("[DEVICE_NOTIFICATION_REPORTED] Full message:", message);
      logger.log(
        "[DEVICE_NOTIFICATION_REPORTED] Notifications array:",
        message.notifications,
      );

      if (message.notifications && Array.isArray(message.notifications)) {
        logger.log(
          `[DEVICE_NOTIFICATION_REPORTED] Processing ${message.notifications.length} notifications`,
        );

        message.notifications.forEach((notification, index) => {
          logger.log(
            `[DEVICE_NOTIFICATION_REPORTED] Notification ${index}:`,
            notification,
          );
          logger.log(
            `[DEVICE_NOTIFICATION_REPORTED] Notification type: ${notification.notificationType}`,
          );

          // Handle Event Denied notifications
          if (notification.notificationType === "EVENT_DENIED") {
            logger.log(
              "[EVENT_DENIED] ✅ Found EVENT_DENIED notification:",
              notification,
            );

            // Only show if this is the currently selected device
            if (message.deviceId === selectedDeviceIdRef.current) {
              logger.log(
                "[EVENT_DENIED] ✅ Device matches currently selected device",
              );

              // Check if device is actually in lockdown before showing modal
              const { deviceStates } = useDevicesStore.getState();
              const deviceState = deviceStates[message.deviceId];
              logger.log("[EVENT_DENIED] Current device state:", deviceState);
              logger.log(
                "[EVENT_DENIED] Lockdown state:",
                deviceState?.lockdownState,
              );

              const isInLockdown = deviceState?.lockdownState === "Lockdown On";

              if (isInLockdown) {
                logger.log(
                  "[EVENT_DENIED] ✅ Device is in lockdown, showing modal",
                );
                setEventDeniedAlert({
                  deviceId: message.deviceId,
                  timestamp: notification.timestamp || new Date().toISOString(),
                  actionType: notification.actionType || "Unknown Action",
                  actionDetails: notification.details || null,
                });
                logger.log("[EVENT_DENIED] 🎯 Modal state set!");
              } else {
                logger.log(
                  "[EVENT_DENIED] ❌ Device is NOT in lockdown (state: " +
                    deviceState?.lockdownState +
                    "), ignoring notification",
                );
              }
            } else {
              logger.log(
                `[EVENT_DENIED] ❌ Ignoring event denied for device ${message.deviceId} ` +
                  `(selected device is ${selectedDeviceIdRef.current})`,
              );
            }
            return; // Continue to next notification in forEach loop
          }

          const { deviceId, details, notificationName } = notification;
          const currentDeviceId = selectedDeviceIdRef.current;

          logger.log(
            `[DEVICE_NOTIFICATION_REPORTED] Notification: deviceId=${deviceId}, ` +
              `notificationName=${notificationName}, details=`,
            details,
          );

          // Track bulk action device responses for lockdown events
          if (
            notificationName === "infoCloudCommandEvent" &&
            details &&
            (details.effect === "Lockdown On" ||
              details.effect === "Lockdown Off")
          ) {
            logger.log(
              `[DEVICE_NOTIFICATION_REPORTED] ✅ MATCHED! Device ${deviceId} lockdown state: ${details.effect}`,
            );

            // Update device lockdown state tracking for bulk actions
            setBulkDeviceLockdownStates((prevStates) => {
              logger.log(
                `[DEVICE_NOTIFICATION_REPORTED] 💾 Setting bulk device lockdown state for ${deviceId}: ${details.effect}`,
              );
              logger.log(
                `[DEVICE_NOTIFICATION_REPORTED] Previous states:`,
                prevStates,
              );
              const newStates = {
                ...prevStates,
                [deviceId]: details.effect,
              };
              logger.log(
                `[DEVICE_NOTIFICATION_REPORTED] New states:`,
                newStates,
              );
              return newStates;
            });

            // Also update deviceStates for display (lockdownState, not lockState)
            setDeviceStates((prevStates) => ({
              ...prevStates,
              [deviceId]: {
                ...(prevStates[deviceId] || {}),
                lockdownState: details.effect,
              },
            }));
          } else {
            logger.log(
              `[DEVICE_NOTIFICATION_REPORTED] ❌ NOT MATCHED - notificationName: ${notificationName}, ` +
                `details.effect: ${details?.effect}`,
            );
          }

          // Handle lock state changes (deadbolt/passage mode changes)
          if (details && details.cause) {
            logger.log(
              `[DEVICE_NOTIFICATION_REPORTED] Lock state change detected: cause=${details.cause}`,
            );

            let lockStateUpdate = null;
            if (details.cause === "Deadbolt Extended") {
              lockStateUpdate = "LOCKED";
            } else if (details.cause === "Deadbolt Retracted") {
              lockStateUpdate = "UNLOCKED";
            } else if (details.cause.includes("Passage")) {
              lockStateUpdate = "PASSAGE";
            } else if (
              details.cause.includes("Secure") ||
              details.cause.includes("SECURED")
            ) {
              lockStateUpdate = "SECURED";
            }

            if (lockStateUpdate) {
              logger.log(
                `[DEVICE_NOTIFICATION_REPORTED] ✅ Updating lockState for ${deviceId}: ${lockStateUpdate}`,
              );
              setDeviceStates((prevStates) => ({
                ...prevStates,
                [deviceId]: {
                  ...(prevStates[deviceId] || {}),
                  lockState: lockStateUpdate,
                },
              }));
            }
          }

          // Handle connectivity status updates
          // Note: ONLINE/OFFLINE connectivity arrives via deviceStateUpdate (backend
          // transforms DEVICE_NOTIFICATION_REPORTED). This path handles the rare case
          // where the raw event reaches the frontend directly (e.g., via DevStream).
          if (details && "connectivityStatus" in details) {
            if (deviceId === currentDeviceId) {
              const stateUpdates = {
                connectivityStatus: details.connectivityStatus,
              };

              setDeviceStates((prevStates) => ({
                ...prevStates,
                [deviceId]: {
                  ...(prevStates[deviceId] || {}),
                  ...stateUpdates,
                },
              }));

              const newTime = new Date().toISOString();
              setConnectivityStatusUpdateTime((prevTimes) => {
                const newTimes = { ...prevTimes, [deviceId]: newTime };
                localStorage.setItem(
                  "connectivityStatusUpdateTimes",
                  JSON.stringify(newTimes),
                );
                return newTimes;
              });
            }
          }
        });
      }
    },
    [
      selectedDeviceIdRef,
      setEventDeniedAlert,
      setBulkDeviceLockdownStates,
      setDeviceStates,
      setConnectivityStatusUpdateTime,
    ],
  );

  return {
    handleDeviceStateUpdate,
    handleDeviceClaimed,
    handleDeviceUnclaimed,
    handleDeviceNotificationReported,
  };
};

// Export helper functions for testing
export { isValidLockMechanismState, isEventDenied };
